// Joana chat shell — a first-party Phoenix Channels client (the realtime leg,
// :7421 /socket). Joana is WaveTalk's OWNED web stack (ADR-0011), so rather than
// pull in the `phoenix` npm package we implement the wire protocol ourselves —
// it's small, well-specified, and keeps the web client dependency-light and fully
// under our control (matching the zero-dep render layer).
//
// This speaks the Phoenix V2 serializer: every frame is a JSON array
//   [join_ref, ref, topic, event, payload]
// `join_ref` ties a frame to a channel's current join; `ref` matches a push to
// its `phx_reply`; a server-initiated event has `ref == null`.
//
// Implemented: connect with query params, exponential-backoff reconnect, a
// heartbeat with dead-connection detection, channel join/leave, push with reply
// matching + timeout, event subscriptions, and automatic channel rejoin after a
// reconnect. The transport (WebSocket) and scheduler (timers) are injected so the
// whole thing is exercised in tests without a browser or real clock.

export type ChannelState = "closed" | "errored" | "joining" | "joined" | "leaving";
export type PushStatus = "ok" | "error" | "timeout";

export interface WebSocketLike {
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: ((ev?: unknown) => void) | null;
  onclose: ((ev?: unknown) => void) | null;
  onmessage: ((ev: { data: string }) => void) | null;
  onerror: ((ev?: unknown) => void) | null;
}

export type WebSocketFactory = (url: string) => WebSocketLike;

/** Injectable timers so tests can drive heartbeats/reconnects deterministically. */
export interface Scheduler {
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
}

export interface SocketOptions {
  /** Static query params appended to the socket URL. */
  params?: Record<string, string>;
  /**
   * Async params resolved fresh for EVERY (re)connect, merged over `params`.
   * This is how single-use WS-ticket auth works: the provider mints a new ticket
   * per connect (a reconnect can't replay the previous one). If it rejects, the
   * connect is aborted and a reconnect is scheduled with backoff.
   */
  connectParams?: () => Promise<Record<string, string>>;
  transport: WebSocketFactory;
  scheduler?: Scheduler;
  heartbeatIntervalMs?: number;
  /** Reconnect backoff schedule (ms), clamped to the last entry. */
  reconnectAfterMs?: (tries: number) => number;
  defaultTimeoutMs?: number;
  log?: (kind: string, msg: string, data?: unknown) => void;
}

type Frame = [string | null, string | null, string, string, unknown];

const VSN = "2.0.0";
const DEFAULT_HEARTBEAT = 30_000;
const DEFAULT_TIMEOUT = 10_000;
const DEFAULT_BACKOFF = [10, 50, 100, 250, 500, 1_000, 2_000, 5_000];

export class Push {
  private sent = false;
  private timeoutHandle: unknown = null;
  private recHooks: Array<{ status: PushStatus; cb: (response: unknown) => void }> = [];
  private receivedResp: { status: PushStatus; response: unknown } | null = null;
  ref: string | null = null;
  refEvent: string | null = null;

  private readonly channel: Channel;
  readonly event: string;
  private payload: unknown;
  private timeout: number;

  constructor(channel: Channel, event: string, payload: unknown, timeout: number) {
    this.channel = channel;
    this.event = event;
    this.payload = payload;
    this.timeout = timeout;
  }

  resend(timeout: number): void {
    this.timeout = timeout;
    this.reset();
    this.send();
  }

  send(): void {
    if (this.hasReceived("timeout")) return;
    this.prepare();
    this.fire();
  }

  /** Allocate the ref + reply hook + timeout, WITHOUT sending the frame. Split
   *  out so the join push can set the channel's join_ref to this ref before the
   *  frame goes out (Phoenix joins use ref == join_ref). */
  prepare(): void {
    if (this.hasReceived("timeout")) return;
    this.startTimeout();
  }

  /** Push the frame. Call after prepare() (and, for a join, after the channel
   *  has adopted this push's ref as its join_ref). */
  fire(): void {
    this.sent = true;
    this.channel.socket.push([
      this.channel.joinRef,
      this.ref,
      this.channel.topic,
      this.event,
      this.payload,
    ]);
  }

  /** Register a hook for a reply status. Fires immediately if already received. */
  receive(status: PushStatus, cb: (response: unknown) => void): this {
    if (this.receivedResp && this.receivedResp.status === status) {
      cb(this.receivedResp.response);
    }
    this.recHooks.push({ status, cb });
    return this;
  }

  /** Convenience: settle as a Promise (resolves on "ok", rejects otherwise). */
  promise(): Promise<unknown> {
    return new Promise((resolve, reject) => {
      this.receive("ok", resolve);
      this.receive("error", (r) => reject(new PushError("error", r)));
      this.receive("timeout", () => reject(new PushError("timeout", null)));
    });
  }

  // --- internal (called by Channel/Socket) ---------------------------------

  reset(): void {
    this.cancelRefEvent();
    this.ref = null;
    this.refEvent = null;
    this.receivedResp = null;
    this.sent = false;
  }

  private startTimeout(): void {
    if (this.timeoutHandle) this.cancelTimeout();
    this.ref = this.channel.socket.makeRef();
    this.refEvent = `chan_reply_${this.ref}`;

    this.channel.on(this.refEvent, (payload) => {
      this.cancelRefEvent();
      this.cancelTimeout();
      this.receivedResp = payload as { status: PushStatus; response: unknown };
      this.matchReceive(this.receivedResp);
    });

    this.timeoutHandle = this.channel.socket.scheduler.setTimeout(() => {
      this.trigger("timeout", {});
    }, this.timeout);
  }

  trigger(status: PushStatus, response: unknown): void {
    this.channel.trigger(this.refEvent!, { status, response });
  }

  private matchReceive({ status, response }: { status: PushStatus; response: unknown }): void {
    this.recHooks.filter((h) => h.status === status).forEach((h) => h.cb(response));
  }

  private cancelRefEvent(): void {
    if (this.refEvent) this.channel.off(this.refEvent);
  }

  private cancelTimeout(): void {
    if (this.timeoutHandle) this.channel.socket.scheduler.clearTimeout(this.timeoutHandle);
    this.timeoutHandle = null;
  }

  hasReceived(status: PushStatus): boolean {
    return this.receivedResp?.status === status;
  }

  get wasSent(): boolean {
    return this.sent;
  }
}

export class PushError extends Error {
  readonly status: PushStatus;
  readonly response: unknown;

  constructor(status: PushStatus, response: unknown) {
    super(`push ${status}`);
    this.status = status;
    this.response = response;
    this.name = "PushError";
  }
}

export class Channel {
  joinRef: string | null = null;
  state: ChannelState = "closed";
  private bindings: Array<{ event: string; ref: number; cb: (payload: unknown) => void }> = [];
  private bindingRef = 0;
  private readonly joinPush: Push;
  private pushBuffer: Push[] = [];
  private joinedOnce = false;
  private rejoinHandle: unknown = null;

  readonly topic: string;
  readonly params: Record<string, unknown>;
  readonly socket: Socket;
  private readonly timeout: number;

  constructor(
    topic: string,
    params: Record<string, unknown>,
    socket: Socket,
    timeout: number,
  ) {
    this.topic = topic;
    this.params = params;
    this.socket = socket;
    this.timeout = timeout;
    this.joinPush = new Push(this, "phx_join", params, timeout);

    this.joinPush.receive("ok", () => {
      this.state = "joined";
      this.flushPushBuffer();
    });
    this.joinPush.receive("error", () => {
      this.state = "errored";
      this.scheduleRejoin();
    });
    this.joinPush.receive("timeout", () => {
      this.state = "errored";
      this.scheduleRejoin();
    });

    // Server-initiated close/error → mark closed/errored and (for error) rejoin.
    this.on("phx_close", () => {
      this.cancelRejoin();
      this.state = "closed";
    });
    this.on("phx_error", () => {
      if (this.state === "leaving" || this.state === "closed") return;
      this.state = "errored";
      this.scheduleRejoin();
    });
  }

  /** Join the topic. Idempotent — returns the (single) join push. If the socket
   *  isn't connected yet, the actual join is sent by the socket's open handler
   *  (rejoinOnReconnect), so we never buffer a join frame or double-join. */
  join(): Push {
    if (this.joinedOnce && this.state === "joined") return this.joinPush;
    this.joinedOnce = true;
    if (this.socket.isConnected()) this.rejoin();
    return this.joinPush;
  }

  leave(): Push {
    this.cancelRejoin();
    this.state = "leaving";
    const leavePush = new Push(this, "phx_leave", {}, this.timeout);
    leavePush.receive("ok", () => {
      this.state = "closed";
    });
    leavePush.send();
    // `this.state` is read back as a FIELD rather than as the literal assigned
    // three lines up: the "ok" callback above can run synchronously and set it
    // to "closed", which is exactly the case this guard exists for. Without the
    // widening the compiler narrows it to "leaving" and calls the comparison
    // unreachable.
    if ((this.state as string) !== "joined") leavePush.trigger("ok", {});
    return leavePush;
  }

  /** Subscribe to an event. Returns a ref usable with off(event, ref). */
  on(event: string, cb: (payload: unknown) => void): number {
    const ref = this.bindingRef++;
    this.bindings.push({ event, ref, cb });
    return ref;
  }

  off(event: string, ref?: number): void {
    this.bindings = this.bindings.filter(
      (b) => !(b.event === event && (ref === undefined || b.ref === ref)),
    );
  }

  /** Send an event on the channel; buffered until joined. */
  push(event: string, payload: unknown, timeout = this.timeout): Push {
    const pushEvent = new Push(this, event, payload, timeout);
    if (this.state === "joined") {
      pushEvent.send();
    } else {
      this.pushBuffer.push(pushEvent);
    }
    return pushEvent;
  }

  /** Dispatch an incoming frame's payload to this channel's bindings. */
  trigger(event: string, payload: unknown): void {
    for (const b of this.bindings) {
      if (b.event === event) b.cb(payload);
    }
  }

  isMember(joinRef: string | null): boolean {
    // A frame with a join_ref from a stale join (after a rejoin) is ignored.
    return joinRef === null || joinRef === this.joinRef;
  }

  // --- internal ------------------------------------------------------------

  private rejoin(): void {
    if (this.state === "leaving") return;
    this.cancelRejoin();
    this.state = "joining";
    // The join push's ref doubles as the channel's join_ref: reset, allocate the
    // ref + reply hook (prepare), adopt it as join_ref, then send the frame.
    this.joinPush.reset();
    this.joinPush.prepare();
    this.joinRef = this.joinPush.ref;
    this.joinPush.fire();
  }

  private scheduleRejoin(): void {
    this.cancelRejoin();
    this.rejoinHandle = this.socket.scheduler.setTimeout(() => {
      if (this.socket.isConnected()) this.rejoin();
    }, this.timeout);
  }

  private cancelRejoin(): void {
    if (this.rejoinHandle) {
      this.socket.scheduler.clearTimeout(this.rejoinHandle);
      this.rejoinHandle = null;
    }
  }

  private flushPushBuffer(): void {
    const buffered = this.pushBuffer;
    this.pushBuffer = [];
    for (const p of buffered) p.send();
  }

  /** Called by the socket when it (re)connects, to re-establish the join. */
  rejoinOnReconnect(): void {
    if (this.joinedOnce && this.state !== "leaving") this.rejoin();
  }
}

export class Socket {
  readonly scheduler: Scheduler;
  private readonly endpoint: string;
  private readonly params: Record<string, string>;
  private readonly connectParams?: () => Promise<Record<string, string>>;
  private connecting = false;
  private readonly transport: WebSocketFactory;
  private readonly heartbeatIntervalMs: number;
  private readonly defaultTimeoutMs: number;
  private readonly reconnectAfterMs: (tries: number) => number;
  private readonly log: (kind: string, msg: string, data?: unknown) => void;

  private conn: WebSocketLike | null = null;
  private connOpen = false;
  private channels: Channel[] = [];
  private sendBuffer: Array<() => void> = [];
  private refCounter = 0;
  private heartbeatHandle: unknown = null;
  private pendingHeartbeatRef: string | null = null;
  private reconnectHandle: unknown = null;
  private reconnectTries = 0;
  private closedGracefully = false;

  private openCallbacks: Array<() => void> = [];
  private closeCallbacks: Array<(ev?: unknown) => void> = [];
  private errorCallbacks: Array<(err?: unknown) => void> = [];

  constructor(endpoint: string, opts: SocketOptions) {
    this.endpoint = endpoint;
    this.params = opts.params ?? {};
    this.connectParams = opts.connectParams;
    this.transport = opts.transport;
    this.scheduler = opts.scheduler ?? globalScheduler();
    this.heartbeatIntervalMs = opts.heartbeatIntervalMs ?? DEFAULT_HEARTBEAT;
    this.defaultTimeoutMs = opts.defaultTimeoutMs ?? DEFAULT_TIMEOUT;
    this.reconnectAfterMs =
      opts.reconnectAfterMs ?? ((tries) => DEFAULT_BACKOFF[Math.min(tries - 1, DEFAULT_BACKOFF.length - 1)]);
    this.log = opts.log ?? (() => {});
  }

  connect(): void {
    if (this.conn || this.connecting) return;
    this.closedGracefully = false;
    // Static-params path stays fully synchronous (existing behaviour). Only the
    // ticket path (connectParams) awaits a fresh ticket before opening.
    if (!this.connectParams) {
      this.openConn(this.params);
      return;
    }
    this.connecting = true;
    Promise.resolve(this.connectParams()).then(
      (dynamic) => {
        this.connecting = false;
        // disconnect() or a connect() may have raced while we fetched the ticket
        if (this.closedGracefully || this.conn) return;
        this.openConn({ ...this.params, ...dynamic });
      },
      (err) => {
        this.connecting = false;
        this.log("transport", "connect params failed", err);
        if (!this.closedGracefully) this.scheduleReconnect();
      },
    );
  }

  private openConn(params: Record<string, string>): void {
    const url = this.buildUrl(params);
    this.log("transport", `connecting ${url}`);
    this.conn = this.transport(url);
    this.conn.onopen = () => this.onConnOpen();
    this.conn.onclose = (ev) => this.onConnClose(ev);
    this.conn.onerror = (err) => this.onConnError(err);
    this.conn.onmessage = (ev) => this.onConnMessage(ev.data);
  }

  disconnect(): void {
    this.closedGracefully = true;
    this.cancelReconnect();
    this.cancelHeartbeat();
    if (this.conn) {
      this.conn.onclose = null; // don't trigger reconnect on a graceful close
      this.conn.close();
      this.conn = null;
    }
  }

  isConnected(): boolean {
    return this.connOpen;
  }

  channel(topic: string, params: Record<string, unknown> = {}): Channel {
    const existing = this.channels.find((c) => c.topic === topic);
    if (existing) return existing;
    const chan = new Channel(topic, params, this, this.defaultTimeoutMs);
    this.channels.push(chan);
    return chan;
  }

  /** Remove a channel entirely (after leave). */
  remove(channel: Channel): void {
    this.channels = this.channels.filter((c) => c !== channel);
  }

  onOpen(cb: () => void): void {
    this.openCallbacks.push(cb);
  }
  onClose(cb: (ev?: unknown) => void): void {
    this.closeCallbacks.push(cb);
  }
  onError(cb: (err?: unknown) => void): void {
    this.errorCallbacks.push(cb);
  }

  makeRef(): string {
    this.refCounter = (this.refCounter + 1) % Number.MAX_SAFE_INTEGER;
    return String(this.refCounter);
  }

  /** Enqueue a frame; flushed immediately if open, else on next open. */
  push(frame: Frame): void {
    const send = () => {
      if (this.conn && this.connOpen) this.conn.send(JSON.stringify(frame));
    };
    if (this.connOpen) send();
    else this.sendBuffer.push(send);
  }

  // --- connection lifecycle ------------------------------------------------

  private onConnOpen(): void {
    this.connOpen = true;
    this.reconnectTries = 0;
    this.cancelReconnect();
    this.flushSendBuffer();
    this.startHeartbeat();
    for (const c of this.channels) c.rejoinOnReconnect();
    for (const cb of this.openCallbacks) cb();
    this.log("transport", "open");
  }

  private onConnClose(ev?: unknown): void {
    this.connOpen = false;
    this.conn = null;
    this.cancelHeartbeat();
    for (const cb of this.closeCallbacks) cb(ev);
    this.log("transport", "close");
    if (!this.closedGracefully) this.scheduleReconnect();
  }

  private onConnError(err?: unknown): void {
    for (const cb of this.errorCallbacks) cb(err);
    this.log("transport", "error", err);
    // A close event follows an error in browsers; reconnect is handled there.
  }

  private onConnMessage(raw: string): void {
    let frame: Frame;
    try {
      frame = JSON.parse(raw) as Frame;
    } catch {
      this.log("transport", "bad frame", raw);
      return;
    }
    const [joinRef, ref, topic, event, payload] = frame;

    // Heartbeat reply clears the pending marker (proof the link is alive).
    if (topic === "phoenix" && ref === this.pendingHeartbeatRef) {
      this.pendingHeartbeatRef = null;
      return;
    }

    for (const chan of this.channels) {
      if (chan.topic !== topic) continue;
      if (!chan.isMember(joinRef)) continue;
      // A reply is delivered as the synthetic chan_reply_<ref> event the Push
      // registered; a server event is delivered under its own name.
      if (event === "phx_reply") {
        chan.trigger(`chan_reply_${ref}`, payload);
      } else {
        chan.trigger(event, payload);
      }
    }
  }

  // --- heartbeat -----------------------------------------------------------

  private startHeartbeat(): void {
    this.cancelHeartbeat();
    this.pendingHeartbeatRef = null;
    this.scheduleHeartbeat();
  }

  private scheduleHeartbeat(): void {
    this.heartbeatHandle = this.scheduler.setTimeout(() => this.sendHeartbeat(), this.heartbeatIntervalMs);
  }

  private sendHeartbeat(): void {
    if (!this.connOpen) return;
    if (this.pendingHeartbeatRef !== null) {
      // Previous heartbeat went unanswered — the link is dead. Drop it; onclose
      // will fire and trigger reconnect.
      this.log("transport", "heartbeat timeout — closing");
      this.pendingHeartbeatRef = null;
      this.conn?.close();
      return;
    }
    this.pendingHeartbeatRef = this.makeRef();
    this.push([null, this.pendingHeartbeatRef, "phoenix", "heartbeat", {}]);
    this.scheduleHeartbeat();
  }

  private cancelHeartbeat(): void {
    if (this.heartbeatHandle) this.scheduler.clearTimeout(this.heartbeatHandle);
    this.heartbeatHandle = null;
  }

  // --- reconnect -----------------------------------------------------------

  private scheduleReconnect(): void {
    this.cancelReconnect();
    this.reconnectTries += 1;
    const delay = this.reconnectAfterMs(this.reconnectTries);
    this.log("transport", `reconnect in ${delay}ms (try ${this.reconnectTries})`);
    this.reconnectHandle = this.scheduler.setTimeout(() => this.connect(), delay);
  }

  private cancelReconnect(): void {
    if (this.reconnectHandle) this.scheduler.clearTimeout(this.reconnectHandle);
    this.reconnectHandle = null;
  }

  private flushSendBuffer(): void {
    const buffered = this.sendBuffer;
    this.sendBuffer = [];
    for (const fn of buffered) fn();
  }

  private buildUrl(params: Record<string, string>): string {
    const base = this.endpoint.replace(/\/+$/, "") + "/websocket";
    const q = new URLSearchParams({ ...params, vsn: VSN });
    return `${base}?${q.toString()}`;
  }
}

function globalScheduler(): Scheduler {
  return {
    setTimeout: (fn, ms) => (globalThis as unknown as {
      setTimeout: (f: () => void, ms: number) => unknown;
    }).setTimeout(fn, ms),
    clearTimeout: (h) => (globalThis as unknown as {
      clearTimeout: (h: unknown) => void;
    }).clearTimeout(h),
  };
}
